import React, { useState, useCallback } from "react";

/**
 * A generic form state hook that manages form data, validation errors,
 * and common operations (updateField, resetForm).
 *
 * @param getInitialData - Stable `useCallback` returning the initial form data.
 *                         Should be memoised by the caller (e.g. via `useCallback`)
 *                         so that the effect only runs when the source data changes.
 * @param validateData   - Pure function that validates the data and returns an
 *                         error map. An empty map means the form is valid.
 *
 * @example
 * ```tsx
 * function validateTrack(data: Partial<CreateTrackRequest>) {
 *   const errors: Record<string, string> = {};
 *   if (!data.name?.trim()) errors.name = "Name is required";
 *   return errors;
 * }
 *
 * const getInitial = useCallback(
 *   () => createInitialFormData(initialTrack, { name: "", ... }),
 *   [initialTrack],
 * );
 *
 * const { data, errors, validate, updateField, resetForm, setData } =
 *   useFormState(getInitial, validateTrack);
 * ```
 */
export function useFormState<T extends Record<string, unknown>>(
  getInitialData: () => T,
  validateData: (data: T) => Record<string, string>,
) {
  const [data, setData] = useState<T>(getInitialData);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Reset whenever the source entity changes (e.g. when navigating to a
  // different record or when an initial value is loaded from the server).
  React.useEffect(() => {
    setData(getInitialData());
    setErrors({});
  }, [getInitialData]);

  const validate = useCallback((): boolean => {
    const newErrors = validateData(data);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [data, validateData]);

  const updateField = useCallback(
    (field: keyof T, value: unknown) => {
      setData((prev) => ({ ...prev, [field]: value }));
      // Clear the error for the changed field immediately so the user gets
      // instant feedback while typing.
      if (errors[field as string]) {
        setErrors((prev) => ({ ...prev, [field as string]: "" }));
      }
    },
    [errors],
  );

  const resetForm = useCallback(() => {
    setData(getInitialData());
    setErrors({});
  }, [getInitialData]);

  return { data, errors, validate, updateField, resetForm, setData };
}
